/**
 * The Society's own documents, in one place.
 *
 * These are read in two surfaces — the invitation, before anyone accepts, and
 * the Codex, where a member goes back to them months later — so the text lives
 * here rather than in either, and cannot drift into two versions that disagree.
 *
 * Why they exist: Arcanum has an unusual number of one-way doors for an
 * application of its size, and until now each was explained in the interface at
 * the moment of doing the thing. That is the worst possible time to learn it,
 * because the member has already decided.
 *
 * Structured by consequence rather than by feature. "The Vault: encrypted
 * notes…" is a manual and nobody remembers a manual. "This cannot be undone" is
 * remembered.
 *
 * House rules belong here and not in the Vault, for a reason worth keeping in
 * mind if anyone is tempted to move them: a member who has forgotten their
 * vault passphrase could not read the document explaining what to do about it.
 * The Vault holds what is yours; the Codex holds what is the Society's.
 *
 * ---------------------------------------------------------------------------
 * EDITING THIS FILE
 *
 * Every piece of text is wrapped in backticks (`) rather than quotes, so an
 * apostrophe can be typed straight into a sentence — the Society's rules, don't
 * do this — without breaking the build. Keep it that way.
 *
 * Two things will break it: a stray backtick inside the text, and the sequence
 * ${ (a dollar followed by a brace), which JavaScript reads as an instruction.
 * Neither is likely in prose.
 *
 * Each paragraph is one entry in its section's `body` list, separated by a
 * comma. Add a paragraph by adding an entry; add a section by copying an
 * existing block. Nothing else needs changing — both surfaces read from here.
 *
 * A change reaches members through a normal deploy. There is no separate step,
 * and members need do nothing but open the app.
 * ---------------------------------------------------------------------------
 */

export const MEMBER_DOCS = [
  {
    id: 'before-you-accept',
    title: `Before you accept`,
    summary: `What Arcanum is, and what it is honest about not being.`,
    // Shown on the invitation as well, where it is part of what is accepted.
    onInvitation: true,
    sections: [
      {
        heading: `What this is`,
        body: [
          `A private society of a few members, entered by invitation only. There is no public sign-up, no way for a stranger to reach it, and no way for a member to bring someone in.`,
          `You have one conversation with the Society, and one shared room where members meet each other. There is no address book, no profiles to browse, and no direct messaging between members. This is deliberate and it is not going to change.`,
        ],
      },
      {
        heading: `The Society can read your thread`,
        body: [
          `Your conversation with the Society is stored so that it can be read, and staff read it. That is not an oversight to be fixed later — it is a channel for asking the Society things, and a channel where nobody can read the question has no purpose.`,
          `Arcanum is not end-to-end encrypted, and we would rather say so here than let you assume otherwise. Traffic is encrypted between your phone and the service, but it is decrypted at the edge of the network we sit behind, as it is for most of the internet.`,
        ],
      },
      {
        heading: `Two things the Society genuinely cannot read`,
        body: [
          `The Private Vault and the Secure Capsule are encrypted by your own browser with keys that never reach us. Not as a matter of policy — as a matter of not having them. Anyone reading our database, our backups, or looking over our shoulder, sees nothing usable.`,
          `With one limit, stated plainly because it is the honest boundary of all encryption delivered through a browser: we serve the code that does the encrypting. A compromised server could serve altered code. This is true of every web-based encrypted messenger, and it is why these two features are described as protecting you from the database rather than from the Society itself.`,
        ],
      },
      {
        heading: `Who knows who you are`,
        body: [
          `In the shared room you appear as your member number and never as your name. Other members do not learn who you are from Arcanum.`,
          `The Founder knows every member. That is what makes the Society a society rather than a forum, and it is not something the software hides from you.`,
          `Your e-mail address is stored, because it is how you sign in. No mail is ever sent by this application — there is no mail system in it at all.`,
        ],
      },
      {
        heading: `On your phone`,
        body: [
          `Notifications say only that a message arrived. They carry no name and no preview, because a notification is read on a locked screen by whoever happens to be holding the phone. You can turn previews on if you would rather have them.`,
          `The app hides itself when you switch away, and asks for your password again after a short absence. Both can be turned off per device.`,
        ],
      },
    ],
  },

  {
    id: 'cannot-be-undone',
    title: `Things that cannot be undone`,
    summary: `The doors that only open one way. Worth reading once, before you meet one.`,
    sections: [
      {
        heading: `A vault with a forgotten passphrase is gone`,
        body: [
          `Your vault is encrypted with a passphrase we have never seen and cannot see. If you forget it, we cannot help you — not reluctantly, not with enough persuasion. There is nothing on our side to recover.`,
          `This is why you are given a recovery key when the vault is created, shown once and never again. Write it down and keep it somewhere other than where you keep your passphrase.`,
          `Forget both and the notes are unreadable permanently. You can reset the vault and start again, but resetting destroys everything in it.`,
        ],
      },
      {
        heading: `Your vault passphrase is not your password`,
        body: [
          `They are two different secrets on purpose. Your password lets you into Arcanum, and we hold a scrambled copy of it. Your vault passphrase decrypts your notes, and we hold nothing.`,
          `So a new password does not open your vault, and having your password reset does not help you into it. Please do not use the same one for both: whoever learned your password would then also be able to read your notes, which is exactly what the vault exists to prevent.`,
        ],
      },
      {
        heading: `A capsule opens once, and opening it uses it up`,
        body: [
          `A Secure Capsule is destroyed by the act of opening it. The server hands over the encrypted message and deletes it in the same breath, so there is no second look, and no copy left behind if something goes wrong at your end afterwards.`,
          `A wrong code costs you nothing — it is checked before the capsule is spent, so you can try again. But once the right code opens it, that is the only time it will ever be read.`,
        ],
      },
      {
        heading: `Never send a capsule code inside Arcanum`,
        body: [
          `The whole point of a capsule is that we hold the locked message and never the key. Send the code in the same conversation and you have handed us both halves, and the feature has protected nothing.`,
          `Say it in person, or by phone, or by any route that is not this one.`,
        ],
      },
      {
        heading: `A disappearing message is not archived first`,
        body: [
          `When a message expires it is deleted, not hidden. If it mattered, keep it somewhere before it goes.`,
        ],
      },
      {
        heading: `Closing a case starts a clock`,
        body: [
          `Either of us can mark a matter finished, and three days later everything in that conversation is removed. It stays readable in the meantime, either of us can reopen it, and writing again reopens it by itself — so a case is never closed out from under a conversation that is still going.`,
          `Nothing closes on its own. A quiet thread is not a finished one, and we would rather leave something sitting there than delete a question you simply had not got back to.`,
        ],
      },
      {
        heading: `What a backup still holds, and for how long`,
        body: [
          `The Society keeps three nightly backups so that a broken disk is not the end of everything. They are encrypted, and they are the reason deletion is not quite instant: something removed today can still exist in a backup for up to three more days before that copy is discarded in its turn.`,
          `Three, deliberately — the same as a closed case lives. A backup that outlived the conversations inside it would not just be storing them; restoring from it would bring them back.`,
        ],
      },
      {
        heading: `And one thing that is not a one-way door`,
        body: [
          `If you forget your password, you are not locked out for good. Ask the Founder and you will be given a single-use link to set a new one. You choose it yourself, so nobody else learns it — not even the Founder.`,
          `This is why there is no "forgot password" e-mail: e-mail is the only thing stored here that identifies you, and we would rather not hand a mail company a record of who is a member.`,
        ],
      },
    ],
  },
];

export const findMemberDoc = (id) => MEMBER_DOCS.find((d) => d.id === id) || null;
